import { Lock, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/app/kit";
import { cn } from "@/lib/utils";

const withDeps = (code, byCode, acc = new Set()) => {
  if (acc.has(code) || !byCode[code]) return acc;
  acc.add(code);
  (byCode[code].dependencies || []).forEach((d) => withDeps(d, byCode, acc));
  return acc;
};

const dependentsOf = (code, selected, byCode, acc = new Set()) => {
  selected.forEach((c) => {
    if (!acc.has(c) && (byCode[c]?.dependencies || []).includes(code)) {
      acc.add(c);
      dependentsOf(c, selected, byCode, acc);
    }
  });
  return acc;
};

export const ModuleSelector = ({ modules, value, onChange, planModules = [], readOnly }) => {
  const byCode = Object.fromEntries(modules.map((m) => [m.code, m]));
  const selected = new Set([...value, ...modules.filter((m) => m.is_core).map((m) => m.code)]);
  const name = (c) => byCode[c]?.name || c;

  const toggle = (m, on) => {
    const next = new Set(selected);
    if (on) {
      const add = withDeps(m.code, byCode);
      const extra = [...add].filter((c) => c !== m.code && !next.has(c));
      add.forEach((c) => next.add(c));
      if (extra.length) toast.info(`Dépendances activées automatiquement : ${extra.map(name).join(", ")}`);
    } else {
      const deps = dependentsOf(m.code, next, byCode);
      next.delete(m.code);
      deps.forEach((c) => next.delete(c));
      if (deps.size) toast.warning(`Modules dépendants également désactivés : ${[...deps].map(name).join(", ")}`);
    }
    onChange([...next]);
  };

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...new Set([...planModules])])} data-testid="modules-apply-plan">Appliquer la formule</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(modules.filter((m) => m.status !== "disabled").map((m) => m.code))} data-testid="modules-select-all">Tout sélectionner</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(modules.filter((m) => m.is_core).map((m) => m.code))} data-testid="modules-select-none">Socle uniquement</Button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {modules.map((m) => {
          const on = selected.has(m.code);
          const off = m.status === "disabled";
          const inPlan = planModules.includes(m.code);
          return (
            <div key={m.code} className={cn("rounded-xl border p-4 flex gap-4 transition-colors", on ? "border-sky-300 bg-sky-50/40" : "border-slate-200 bg-white")} data-testid={`module-card-${m.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-sm text-slate-900">{m.name}</p>
                  <span className="font-mono text-[11px] text-slate-400">{m.code}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{m.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {m.is_core && <Tag tone="indigo"><Lock className="h-3 w-3" />Socle</Tag>}
                  {!m.is_core && (inPlan ? <Tag tone="sky">Inclus dans la formule</Tag> : on && <Tag tone="amber">Ajout hors formule</Tag>)}
                  {!m.is_core && inPlan && !on && <Tag tone="red">Retiré de la formule</Tag>}
                  {!m.implemented && <Tag tone="gray">En développement</Tag>}
                  {off && <Tag tone="red">Désactivé sur la plateforme</Tag>}
                  {(m.dependencies || []).length > 0 && <Tag tone="gray"><Link2 className="h-3 w-3" />Requiert {m.dependencies.map(name).join(", ")}</Tag>}
                </div>
              </div>
              <Switch checked={on} disabled={readOnly || m.is_core || off} onCheckedChange={(v) => toggle(m, v)} data-testid={`module-toggle-${m.code}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
